import React,{useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiTarget,FiCalendar,FiTrendingUp,FiPlus,FiX,FiInfo,FiEdit3,FiCheck}=FiIcons;

const GoalSetting=({formData,updateFormData,setCurrentStep})=> {
  const navigate=useNavigate();
  const [activeTab,setActiveTab]=useState('monthly');
  const [showSmartInfo,setShowSmartInfo]=useState(false);
  const [goals,setGoals]=useState({
    monthly: formData.monthlyGoals || [],
    sixMonth: formData.sixMonthGoals || [],
    yearly: formData.yearlyGoals || []
  });
  const [customGoal,setCustomGoal]=useState('');
  const [editingGoal,setEditingGoal]=useState({timeframe: null,index: null,text: ''});

  // Base suggested goals
  const baseSuggestedGoals={
    monthly: [
      'Gather 100 leads to my email list within the next 30 days',
      'Post 20 educational content pieces on social media',
      'Reach out to 50 potential students through networking',
      'Create and launch one lead magnet (free resource)',
      'Establish partnerships with 2 local businesses or schools',
      'Build my teacher page and online presence for better global reach', // Updated line
      'Host 2 free webinars or workshops',
      'Get 10 positive reviews or testimonials'
    ],
    sixMonth: [
      'Increase the number of students by 5 by the end of the 6 months period',
      'Launch a signature online course or program',
      'Build an email list of 500 engaged subscribers',
      'Establish myself as a thought leader with 50 published articles/videos',
      'Create a referral program that generates 20% of new students',
      'Increase monthly revenue by 30%',
      'Develop partnerships with 5 complementary service providers',
      'Achieve a 90% student satisfaction rate'
    ],
    yearly: [
      'Double my current student base within 12 months',
      'Generate $50,000+ in annual revenue from teaching',
      'Launch a premium coaching or mentorship program',
      'Speak at 5 industry conferences or events',
      'Write and publish an educational book or guide',
      'Build a team of 2-3 assistant tutors or coaches',
      'Establish an online academy with multiple courses',
      'Achieve recognition as a top educator in my niche'
    ]
  };

  // Generate dynamic goals based on leads and prospects data
  const generateDynamicGoals=()=> {
    const dynamicGoals={...baseSuggestedGoals};

    // Adjust goals based on leads data
    if (formData.estimatedLeads) {
      if (formData.estimatedLeads==='Not sure' || formData.estimatedLeads==='0-50') {
        dynamicGoals.monthly[0]='Build my first 100 leads through targeted content marketing';
        dynamicGoals.sixMonth[2]='Grow my email list to 250 engaged subscribers';
      } else if (formData.estimatedLeads==='50-100') {
        dynamicGoals.monthly[0]='Double my leads to 200 through optimized lead magnets';
        dynamicGoals.sixMonth[2]='Expand my email list to 500 engaged subscribers';
      } else if (formData.estimatedLeads==='100-200') {
        dynamicGoals.monthly[0]='Increase leads by 50% through social media campaigns';
        dynamicGoals.sixMonth[2]='Grow my email list to 750 engaged subscribers';
      } else if (formData.estimatedLeads==='200-500') {
        dynamicGoals.monthly[0]='Convert 25% of leads to prospects through nurturing campaigns';
        dynamicGoals.sixMonth[2]='Build an email list of 1,000 engaged subscribers';
      } else if (formData.estimatedLeads==='500+') {
        dynamicGoals.monthly[0]='Convert 30% of leads to qualified prospects';
        dynamicGoals.sixMonth[2]='Expand my email list to 1,500 engaged subscribers';
      }
    }

    // Adjust goals based on prospects data
    if (formData.estimatedProspects) {
      if (formData.estimatedProspects==='Not sure' || formData.estimatedProspects==='0-50') {
        dynamicGoals.monthly[3]='Create a lead magnet to convert visitors to prospects';
        dynamicGoals.sixMonth[4]='Develop a lead nurturing sequence to convert 25% of leads to prospects';
      } else if (formData.estimatedProspects==='50-100') {
        dynamicGoals.monthly[3]='Improve lead nurturing to increase prospect conversion rate by 20%';
        dynamicGoals.sixMonth[4]='Create a sales funnel to convert 40% of prospects to students';
      } else if (formData.estimatedProspects==='100-200') {
        dynamicGoals.monthly[3]='Optimize prospect conversion with targeted offers';
        dynamicGoals.sixMonth[4]='Implement a CRM system to track and convert 50% of prospects';
      } else if (formData.estimatedProspects==='200-500') {
        dynamicGoals.monthly[3]='Develop premium offers for high-value prospects';
        dynamicGoals.sixMonth[4]='Create a VIP program for top prospects to increase conversion';
      } else if (formData.estimatedProspects==='500+') {
        dynamicGoals.monthly[3]='Launch exclusive offers for qualified prospects';
        dynamicGoals.sixMonth[4]='Establish a VIP client program to maximize prospect value';
      }
    }

    return dynamicGoals;
  };

  const suggestedGoals=generateDynamicGoals();

  const addGoal=(goalText,timeframe)=> {
    if (goalText.trim()) {
      setGoals(prev=> ({
        ...prev,
        [timeframe]: [...prev[timeframe],goalText.trim()]
      }));
      setCustomGoal('');
    }
  };

  const removeGoal=(index,timeframe)=> {
    setGoals(prev=> ({
      ...prev,
      [timeframe]: prev[timeframe].filter((_,i)=> i !==index)
    }));
  };

  const startEditing=(index,timeframe)=> {
    setEditingGoal({
      timeframe,
      index,
      text: goals[timeframe][index]
    });
  };

  const saveEdit=()=> {
    if (editingGoal.text.trim()) {
      setGoals(prev=> ({
        ...prev,
        [editingGoal.timeframe]: prev[editingGoal.timeframe].map((goal,index)=> 
          index===editingGoal.index ? editingGoal.text.trim() : goal
        )
      }));
      setEditingGoal({timeframe: null,index: null,text: ''});
    }
  };

  const cancelEdit=()=> {
    setEditingGoal({timeframe: null,index: null,text: ''});
  };

  const handleNext=()=> {
    updateFormData({
      monthlyGoals: goals.monthly,
      sixMonthGoals: goals.sixMonth,
      yearlyGoals: goals.yearly
    });
    setCurrentStep(4);
    navigate('/action-plan');
  };

  const handlePrevious=()=> {
    setCurrentStep(2);
    navigate('/niche');
  };

  const hasGoals=goals.monthly.length > 0 || goals.sixMonth.length > 0 || goals.yearly.length > 0;

  const tabs=[
    {id: 'monthly',label: 'Monthly Goals',icon: FiCalendar},
    {id: 'sixMonth',label: '6-Month Goals',icon: FiTrendingUp},
    {id: 'yearly',label: 'Yearly Goals',icon: FiTarget}
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{opacity: 0,y: 30}}
        animate={{opacity: 1,y: 0}}
        className="bg-white rounded-2xl shadow-xl p-8"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <SafeIcon icon={FiTarget} className="text-2xl text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Set Your S.M.A.R.T Goals</h2>
          <p className="text-gray-600">Define specific,measurable goals for different timeframes</p>
        </div>

        {/* S.M.A.R.T Goals Info */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mb-8">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1">
              <SafeIcon icon={FiInfo} className="text-blue-600 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">What are S.M.A.R.T Goals?</h3>
                <div className="grid md:grid-cols-5 gap-4 text-sm">
                  <div>
                    <span className="font-semibold text-blue-600">S</span>pecific
                    <p className="text-gray-600">Clear and well-defined</p>
                  </div>
                  <div>
                    <span className="font-semibold text-blue-600">M</span>easurable
                    <p className="text-gray-600">Trackable with numbers</p>
                  </div>
                  <div>
                    <span className="font-semibold text-blue-600">A</span>chievable
                    <p className="text-gray-600">Realistic and attainable</p>
                  </div>
                  <div>
                    <span className="font-semibold text-blue-600">R</span>elevant
                    <p className="text-gray-600">Aligned with your niche</p>
                  </div>
                  <div>
                    <span className="font-semibold text-blue-600">T</span>ime-bound
                    <p className="text-gray-600">Has a deadline</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-8 bg-gray-100 rounded-lg p-1">
          {tabs.map(tab=> (
            <button
              key={tab.id}
              onClick={()=> setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-semibold transition-all ${
                activeTab===tab.id 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              <SafeIcon icon={tab.icon} className="text-lg" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Goal Content */}
        <div className="space-y-6">
          {/* Suggested Goals */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-4">
              Suggested {tabs.find(t=> t.id===activeTab)?.label}
            </h3>
            <div className="grid gap-3">
              {suggestedGoals[activeTab].map((goal,index)=> (
                <motion.div
                  key={index}
                  initial={{opacity: 0,x: -20}}
                  animate={{opacity: 1,x: 0}}
                  transition={{delay: index * 0.05}}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors"
                >
                  <span className="text-gray-700 flex-1">{goal}</span>
                  <button
                    onClick={()=> addGoal(goal,activeTab)}
                    disabled={goals[activeTab].includes(goal)}
                    className={`ml-4 p-2 rounded-lg transition-all ${
                      goals[activeTab].includes(goal)
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                    }`}
                  >
                    <SafeIcon icon={FiPlus} />
                  </button>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Custom Goal Input */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-4">Add Your Own Goal</h3>
            <div className="flex space-x-3">
              <input
                type="text"
                value={customGoal}
                onChange={(e)=> setCustomGoal(e.target.value)}
                placeholder="Enter your custom goal..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                onKeyPress={(e)=> e.key==='Enter' && addGoal(customGoal,activeTab)}
              />
              <button
                onClick={()=> addGoal(customGoal,activeTab)}
                disabled={!customGoal.trim()}
                className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                  customGoal.trim()
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Add Goal
              </button>
            </div>
          </div>

          {/* Selected Goals */}
          {goals[activeTab].length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-800 mb-4">
                Your Selected {tabs.find(t=> t.id===activeTab)?.label}
              </h3>
              <div className="space-y-2">
                {goals[activeTab].map((goal,index)=> (
                  <motion.div
                    key={index}
                    initial={{opacity: 0,scale: 0.95}}
                    animate={{opacity: 1,scale: 1}}
                    className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg"
                  >
                    {editingGoal.timeframe===activeTab && editingGoal.index===index ? (
                      <div className="flex items-center space-x-3 flex-1">
                        <input
                          type="text"
                          value={editingGoal.text}
                          onChange={(e)=> setEditingGoal(prev=> ({...prev,text: e.target.value}))}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          onKeyPress={(e)=> e.key==='Enter' && saveEdit()}
                          autoFocus
                        />
                        <button
                          onClick={saveEdit}
                          className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-all"
                          title="Save"
                        >
                          <SafeIcon icon={FiCheck} />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
                          title="Cancel"
                        >
                          <SafeIcon icon={FiX} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="text-green-800 flex-1">{goal}</span>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={()=> startEditing(index,activeTab)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-all"
                            title="Edit goal"
                          >
                            <SafeIcon icon={FiEdit3} />
                          </button>
                          <button
                            onClick={()=> removeGoal(index,activeTab)}
                            className="p-2 text-red-500 hover:bg-red-100 rounded-lg transition-all"
                            title="Remove goal"
                          >
                            <SafeIcon icon={FiX} />
                          </button>
                        </div>
                      </>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="mt-8 flex justify-between">
          <motion.button
            whileHover={{scale: 1.05}}
            whileTap={{scale: 0.95}}
            onClick={handlePrevious}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
          >
            Previous
          </motion.button>
          <motion.button
            whileHover={{scale: 1.05}}
            whileTap={{scale: 0.95}}
            onClick={handleNext}
            disabled={!hasGoals}
            className={`px-8 py-3 rounded-lg font-semibold transition-all ${
              hasGoals
                ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Next: Create Action Plan
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default GoalSetting;